import { Children, type ReactNode } from "react";
import { styled } from "@pigment-css/react";

const Section = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: theme.fieldSalesTwo.spacing[24],
}));

const SectionHeader = styled("h2")(({ theme }) => ({
  margin: 0,
  padding: `0 ${theme.fieldSalesTwo.spacing[20]} 0 ${theme.fieldSalesTwo.spacing[32]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

// Inset divider: drawn as ::after starting at spacing[32] (leading checkbox + gap in the
// row layout) so it aligns with the row text. Data-attribute-driven so the container stays
// agnostic to its children:
//   - data-has-footer="false" → divider on every child except the last.
//   - data-has-footer="true" → the footer slot is the last child; exclude it and the row
//     above it. New rows are appended before the footer, so a previously-excluded row moves
//     out of the last-two window and its divider returns.
const GlassContainer = styled("div")(({ theme }) => {
  const dividerLine = {
    content: '""',
    position: "absolute",
    // Both offsets carry the row's hover-bleed inset on top of the text-column alignment.
    left: `calc(${theme.fieldSalesTwo.spacing[32]} + ${theme.fieldSalesTwo.spacing[12]})`,
    right: theme.fieldSalesTwo.spacing[12],
    bottom: 0,
    height: theme.strokeWidth.thin,
    background: theme.fieldSalesTwo.colors.neutral[200],
  } as const;

  return {
    margin: `0 ${theme.fieldSalesTwo.spacing[16]}`,
    padding: theme.fieldSalesTwo.spacing[20],
    borderRadius: theme.fieldSalesTwo.radius[38],
    background: theme.fieldSalesTwo.colors.surface.glass,
    border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    '&[data-dividers="true"][data-has-footer="false"] > :not(:last-child)': { position: "relative" },
    '&[data-dividers="true"][data-has-footer="false"] > :not(:last-child)::after': dividerLine,
    '&[data-dividers="true"][data-has-footer="true"] > :not(:nth-last-child(-n+2))': { position: "relative" },
    '&[data-dividers="true"][data-has-footer="true"] > :not(:nth-last-child(-n+2))::after': dividerLine,
  };
});

interface TodoSectionProps {
  title: string;
  children?: ReactNode;
  /** Draws inset dividers between rows. Disable when rows have no leading checkbox for the
   * inset to align to. */
  showDividers?: boolean;
  /** Non-row content rendered as the card's last child. No divider is drawn between it and
   * the row above. */
  footer?: ReactNode;
}

export function TodoSection({ title, children, showDividers = true, footer }: TodoSectionProps) {
  const hasChildren = Children.count(children) > 0;
  const hasFooter = Boolean(footer);
  if (!hasChildren && !hasFooter) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>{title}</SectionHeader>
      <GlassContainer data-dividers={String(showDividers)} data-has-footer={String(hasFooter)}>
        {children}
        {footer}
      </GlassContainer>
    </Section>
  );
}
